import {
  useCallback,
  useContext,
  useLayoutEffect,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import appPageCopy from '@/i18n/ns/app-page';
import appRuntimeErrorsCopy from '@/i18n/ns/app-runtime-errors';
import appSharingCopy from '@/i18n/ns/app-sharing';
import viewFreshnessCopy from '@/i18n/ns/view-freshness';
import { apiUrl } from '@/lib/shell';
import { ControlRefused, control, takeBootstrap } from './runtime-control';
import { CanvasSettledContext } from '../../artifacts/canvas-settle';
import { useNativeData } from '../data/use-native-data';
import { AppChecksMount, type CheckReportReader } from '../checks/app-checks-mount';
import { createCheckReportApi, type CheckSelection } from '../checks/check-report-api';
import { useAppSharing } from '../sharing';
import { CompletedAppVersions } from '../versions/completed-versions';
import { AppSourcesSection } from '../sources/app-sources-section';
import { createAppSourcesApi, type AppSourcesApi } from '../sources/app-sources-api';
import { AppControlsMount } from '../tracker/app-controls-mount';
import { createRuntimeOperations } from './operation-client';
import type { CachedSeed, CachedView } from '../../../../src/apps/runtime/cached-read-session.mjs';
import { CanvasHeaderTools } from '../../canvas-footer';
import { ViewFreshness } from '../../view-freshness';
import { appRuntimeErrorKind, appRuntimeErrorMessage } from './runtime-error-copy';
import { useNativeWriteApproval } from './use-native-write-approval';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { mountNamedRuntime } from '../../../../src/apps/runtime/host-frame.mjs';
import {
  RuntimeFrame,
  type MountNamedRuntime,
  type RuntimeHandlers,
  type RuntimeIssued,
} from '../../artifacts/site/runtime-frame';
import { useOptionalWorkspace } from '../../context';
import { AppCannotOpen, AppOpening } from '../app-opening';
import type {
  SelectedAppHost,
  SelectedAppHostInput,
  UseSelectedAppHost,
} from '../app-saved-display';
import { DeckExport } from '../preview/deck-export';

/**
 * The selected saved App's host (APP's `registerSelectedAppHost` seam). It supplies ONLY what exists:
 * the App's own isolated runtime frame, issued for this exact saved selection and bound to the session it
 * was opened in, plus a Back to the Library. Data, History and Versions have no producer here yet, so they
 * are null (shown as unavailable, never as empty rows), and no publish/share/reset/duplicate handler is
 * invented: AppPage shows no control for an absent handler. The runtime's data operations are not
 * registered either, so the App reads nothing until APP/DATA register them.
 */
type Issue =
  // `preparing`: the version's safety check is still running for this viewer.
  | { status: 'loading'; preparing?: boolean }
  // `attempt` names the open this document came from: a reopen is always a new frame. `quiet`: a Refresh reopened
  // it, so it opens behind the shown view with no opening stage, at the reader's scroll position.
  | { status: 'ready'; issued: RuntimeIssued; attempt: number; quiet: boolean }
  // Only the saved reader's explicit missing-output refusal describes an unbuilt version.
  | { status: 'none-built' }
  | { status: 'failed'; retryable: boolean; code?: unknown; reason?: string | null };

/**
 * How long an issued frame may stay unreported. A frame whose document or assets cannot load (blocked, refused,
 * unreachable) sends neither runtime.ready nor runtime.error, so without this it would stay blank for good.
 */
export const READY_TIMEOUT_MS = 30_000;

const retryableRefusal = (error: unknown) =>
  !(error instanceof ControlRefused) ||
  error.status >= 500 ||
  error.status === 408 ||
  error.status === 429 ||
  error.code === 'app_check_pending' ||
  error.code === 'app_check_failed';

// A version that failed its safety check: why (its kind of finding), for its
// owner and anyone opening it; the Change with Chat action fixes it.
function checkMessage(
  code: unknown,
  reason: string | null | undefined,
  copy: Readonly<Record<string, string>>,
): string | null {
  if (code === 'app_check_failed') return copy.checkUnavailable;
  if (code !== 'app_check_blocked') return null;
  if (reason === 'secret') return copy.checkBlockedSecret;
  if (reason === 'dependency') return copy.checkBlockedDependency;
  return copy.checkBlocked;
}

/** While the server prepares an App's Check in the background, the host keeps opening and asks again. */
export const CHECK_RETRY_MS = 1500;
const CHECK_RETRIES = 80;

function shaped(value: unknown): RuntimeIssued | null {
  const v = value as Partial<RuntimeIssued> | null;
  return v &&
    typeof v.ticket === 'string' &&
    typeof v.bootstrapUrl === 'string' &&
    typeof v.expiresAt === 'string' &&
    v.scope &&
    typeof v.scope === 'object'
    ? (v as RuntimeIssued)
    : null;
}

/** The exact saved selection the frame is opened for; sent on every control call, never inferred. */
type Selection = { artifactId: string; revisionId: string; versionId: string; purpose: string };

/** A table or saved function name as the App gives it (the SDK's own id bound). */
const syncName = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= 200;

/** The App's sync as it names it: its table and the saved function that syncs it. */
type SyncTarget = { tableId: string; name: string };
/** Sync now's original: the key the server knows it by (none for a run it found but could not name). */
type SyncOriginal = SyncTarget & { requestId?: string };
/** A native sync reply: idle, unknown, failed (with its code) or ok (with only its verified syncs). */
type SyncReply = {
  ok?: unknown;
  error?: { code?: unknown };
  status?: unknown;
  requestId?: unknown;
  code?: unknown;
  syncs?: unknown;
} | null;

const syncInput = (target: SyncTarget, mode: 'start' | 'observe', requestId?: string) => ({
  name: target.name,
  input: {},
  tableId: target.tableId,
  mode,
  ...(requestId ? { operationId: requestId } : {}),
});

/** When the reply's run verifiably synced the table: never a click, a queued or an unknown run. */
const verifiedSync = (reply: SyncReply, tableId: string) =>
  reply?.status === 'ok' && Array.isArray(reply.syncs)
    ? ((reply.syncs as { tableId?: unknown; lastSyncedAt?: unknown }[])
        .filter((entry) => entry?.tableId === tableId && typeof entry.lastSyncedAt === 'string')
        .at(-1)?.lastSyncedAt as string | undefined)
    : undefined;

/** The original a reply leaves kept: a settled run (ok or failed), or none pending (idle), frees it; an unknown one
 * stays, under the key the server names (another tab's run included). */
function nextOriginal(
  kept: SyncOriginal | null,
  target: SyncTarget,
  reply: SyncReply,
  sent?: string,
): SyncOriginal | null {
  if (reply?.status === 'ok' || reply?.status === 'failed' || reply?.status === 'idle') return null;
  if (reply?.status !== 'unknown') return kept;
  const requestId = typeof reply.requestId === 'string' ? reply.requestId : sent;
  return { ...target, ...(requestId ? { requestId } : {}) };
}

/** The Last synced line: the age it says, and how the last Sync now went. */
type SyncedLine = {
  fetchedAt: string | null;
  status: 'idle' | 'refreshing' | 'failed';
  /** A failed sync not known to have failed (it may have run): said as not confirmed. */
  unconfirmed?: boolean;
  code?: unknown;
};

/** The line after a document's look. A look a click has overtaken (one started or answered since it asked) changes
 * nothing; no answer keeps what the line said, with the table's age; an unknown or failed run is said. */
function lookedLine(
  shown: SyncedLine | null,
  at: string | null,
  reply: SyncReply,
  overtaken: { now: boolean; syncing: boolean },
): SyncedLine {
  if (overtaken.now)
    return shown ?? { fetchedAt: at, status: overtaken.syncing ? 'refreshing' : 'idle' };
  if (!reply || reply.ok === false) return { ...(shown ?? { status: 'idle' }), fetchedAt: at };
  if (reply.status === 'unknown' || reply.status === 'failed')
    return {
      fetchedAt: at,
      status: 'failed',
      unconfirmed: reply.status === 'unknown',
      code: reply.code,
    };
  return { fetchedAt: at, status: 'idle' };
}

const NO_CACHED_VIEW: CachedView = Object.freeze({ fetchedAt: null, status: 'idle' });
const noCachedView = () => () => {};
const noCachedSnapshot = () => NO_CACHED_VIEW;

function SavedAppFrame({
  owner,
  selection,
  title,
  session,
  mount,
  onInvalidated,
  onDeck,
  canSync = false,
  readyTimeoutMs = READY_TIMEOUT_MS,
}: {
  readyTimeoutMs?: number;
  /** This viewer may start owner work (its owner or an editor): Sync now is theirs; others see the age only. */
  canSync?: boolean;
  /** The issued document is a chat-built Deck: the page presents it as a deck, not as an App. */
  onDeck?: (deck: boolean) => void;
  /** The account and company the frame was opened for (a page's prefetched ticket is theirs only). */
  owner: { companyId: string; actorId: string };
  selection: Selection;
  title: string;
  session: typeof fetch;
  mount: MountNamedRuntime;
  onInvalidated: () => void;
}) {
  const { artifactId, revisionId, versionId, purpose } = selection;
  const copy = useNamespaceCopy(appPageCopy);
  const errorCopy = useNamespaceCopy(appRuntimeErrorsCopy);
  const sharingCopy = useNamespaceCopy(appSharingCopy);
  const freshnessCopy = useNamespaceCopy(viewFreshnessCopy);
  const locale = useChatInterfaceLocale();
  const [issue, setIssue] = useState<Issue>({ status: 'loading' });
  const settled = useContext(CanvasSettledContext);
  // Presentation follows the current locale without replacing the document's
  // transport/cache, which the mounted frame captured at ticket admission.
  const presentation = useRef({ locale, errorCopy, settled });
  presentation.current = { locale, errorCopy, settled };
  // A refusal has shown too: the canvas loads what it deferred.
  useEffect(() => {
    if (issue.status === 'none-built' || issue.status === 'failed') settled?.();
  }, [issue.status, settled]);
  const [attempt, setAttempt] = useState(0);
  // Consecutive bootstrap answers that the App's Check is still being prepared.
  const checkWaits = useRef(0);
  // A Refresh that reopens keeps the shown frame on screen until the reopened one reports ready: no opening stage.
  const [held, setHeld] = useState<{
    issued: RuntimeIssued;
    handlers: RuntimeHandlers;
    attempt: number;
    quiet: boolean;
    /** When the held view's data was fetched: the bar keeps saying it until the reopened view has shown. */
    fetchedAt: string | null;
  } | null>(null);
  // Where the reader last was in the shown document, so a Refresh reopens there.
  const shownScroll = useRef(0);
  // The App's sync, as the App names it (its table and the saved function that syncs it), and its last verified sync.
  const [sync, setSync] = useState<SyncTarget | null>(null);
  const [synced, setSynced] = useState<SyncedLine | null>(null);
  // Sync now's one original, by the key the server knows it by, until it verifiably settles. An unknown, lost or
  // refused reply keeps it (a reopened document too), so every later Sync now only asks for that same run and never
  // starts a second one. Only an ok or failed run frees it. No key: a run the server found but could not name.
  const syncOriginal = useRef<SyncOriginal | null>(null);
  const keptFor = (target: SyncTarget) => {
    const kept = syncOriginal.current;
    return kept?.name === target.name && kept.tableId === target.tableId ? kept : null;
  };
  // A Sync now waiting for its reply: no document's look overrides it, and the line keeps saying Syncing….
  const syncing = useRef(false);
  // Counts each Sync now's start and its answer: a document's look asked before either is out of date, even when
  // what is kept looks the same again (nothing kept, then a run that failed).
  const syncActivity = useRef(0);
  const holding = useRef(false);
  const release = useCallback(() => {
    holding.current = false;
    setHeld(null);
  }, []);
  // A reopen that fails at any step (bootstrap, a silent frame, an error or a denied read) drops the held view, so a
  // Try again opens as usual.
  useEffect(() => {
    if (issue.status === 'failed' || issue.status === 'none-built') release();
  }, [issue.status, release]);
  const alive = useRef(true);
  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (!holding.current) setIssue({ status: 'loading' });
    // The page that opened this App may already have asked for its ticket, alongside the saved view.
    const prefetched =
      attempt === 0
        ? takeBootstrap(session, { ...owner, artifactId, revisionId, versionId, purpose })
        : null;
    (prefetched ?? control(session, artifactId, 'bootstrap', { revisionId, versionId, purpose }))
      .then((value) => {
        const issued = shaped(value);
        // The issued document must be exactly the selected Artifact and revision (the fields its scope
        // exposes); anything else is another document and is refused, never mounted.
        const scope = issued?.scope as { artifactId?: unknown; revisionId?: unknown } | undefined;
        const same = scope?.artifactId === artifactId && scope?.revisionId === revisionId;
        if (!alive.current) return;
        checkWaits.current = 0;
        setIssue(
          issued && same
            ? { status: 'ready', issued, attempt, quiet: holding.current }
            : { status: 'failed', retryable: false },
        );
      })
      .catch((error) => {
        if (!alive.current) return;
        if (error instanceof ControlRefused && error.code === 'runtime_output_unavailable')
          setIssue({ status: 'none-built' });
        else if (
          error instanceof ControlRefused &&
          error.code === 'app_check_pending' &&
          checkWaits.current < CHECK_RETRIES
        ) {
          // The App is being checked off the open path: say it is getting ready and ask again.
          checkWaits.current += 1;
          setIssue({ status: 'loading', preparing: true });
          window.setTimeout(() => {
            if (alive.current) setAttempt((n) => n + 1);
          }, CHECK_RETRY_MS);
        } else
          setIssue({
            status: 'failed',
            retryable: retryableRefusal(error),
            code: (error as { code?: unknown }).code,
            reason: error instanceof ControlRefused ? error.reason : null,
          });
      });
  }, [
    session,
    owner.companyId,
    owner.actorId,
    artifactId,
    revisionId,
    versionId,
    purpose,
    attempt,
  ]);
  // The issued document this frame was opened for: a runtime.error counts only from that document.
  const issuedTicket = issue.status === 'ready' ? issue.issued.ticket : null;
  const issuedNonce =
    issue.status === 'ready'
      ? String((issue.issued.scope as { documentNonce?: string }).documentNonce ?? '')
      : '';
  const writeApproval = useNativeWriteApproval({
    session,
    apiUrl,
    selection,
    nonce: issuedNonce,
    title,
  });
  // The ticket whose document has reported ready; a frame that never does is shown as unable to open.
  const readyFor = useRef<string | null>(null);
  // The same, for work that must wait for it: a document's ticket is redeemed (its reads allowed) only once it opens.
  const [readyTicket, setReadyTicket] = useState<string | null>(null);
  useEffect(() => {
    if (!issuedTicket) return;
    const timer = window.setTimeout(() => {
      if (!alive.current || readyFor.current === issuedTicket) return;
      setIssue((now) =>
        now.status === 'ready' && now.issued.ticket === issuedTicket
          ? { status: 'failed', retryable: true }
          : now,
      );
    }, readyTimeoutMs);
    return () => window.clearTimeout(timer);
  }, [issuedTicket, readyTimeoutMs]);
  const quiet = issue.status === 'ready' && issue.quiet;
  // The bootstrap's admitted cached reads for this exact document (none from a server without the view cache).
  const cachedSeed =
    issue.status === 'ready'
      ? (issue.issued as RuntimeIssued & { cachedView?: CachedSeed }).cachedView
      : undefined;
  const { handlers, syncOperation, cache, invalidSeed } = useMemo(() => {
    let operations: ReturnType<typeof createRuntimeOperations>;
    try {
      operations = createRuntimeOperations({
        session,
        apiUrl,
        selection: { artifactId, revisionId, versionId, purpose },
        documentNonce: () => issuedNonce,
        approveWrite: writeApproval.approve,
        // An App that prints a failure prints the reader's sentence, never a code or [object Object].
        errorMessage: (code) => appRuntimeErrorMessage(code, presentation.current.errorCopy),
        privateChatApp: () =>
          issue.status === 'ready' &&
          (issue.issued as RuntimeIssued & { privateChatApp?: boolean }).privateChatApp === true,
        // Access to the cached view is gone: drop it and show the existing cannot-open state, never old data.
        cache: cachedSeed
          ? {
              seed: cachedSeed,
              onDenied: (error) => {
                const failure = error as { code?: unknown; name?: unknown };
                if (alive.current)
                  setIssue({
                    status: 'failed',
                    retryable: false,
                    code: failure?.code ?? failure?.name,
                  });
              },
            }
          : undefined,
      });
    } catch {
      // A malformed admitted seed must not crash the workspace or dispatch
      // an uncached substitute. Keep this document withheld and fail closed.
      return {
        handlers: {} as RuntimeHandlers,
        syncOperation: undefined,
        cache: undefined,
        invalidSeed: true,
      };
    }
    // Sync now's start/observe is the host's own: the App names its sync, it never runs this operation itself.
    const { 'functions.sync': syncOperation, ...appOperations } = operations.handlers;
    return {
      invalidSeed: false,
      cache: operations.cache,
      syncOperation,
      handlers: {
        // The read-only SDK operations: the captured selected tuple and THIS issued document's nonce, over the
        // frame's own bound session. An unbound server answers unavailable; nothing is invented here.
        ...appOperations,
        // How current the cached view is, for the App's own data layer: same document, no remount. Only a
        // bootstrap that carried a cached view has one; nothing is invented for any other App.
        ...(operations.cache
          ? {
              'runtime.cached-view': {
                subscribe: (_args: unknown, emit: (event: CachedView) => void) =>
                  operations.cache!.subscribe(emit),
              },
            }
          : {}),
        // The shown document reports where the reader is; a Refresh-reopened one asks for it. Only a position.
        'runtime.scroll': {
          request: async (input: unknown) => {
            const y = Number((input as { y?: unknown } | null)?.y);
            if (Number.isFinite(y) && y >= 0) shownScroll.current = Math.min(y, 10_000_000);
            return null;
          },
        },
        'runtime.view-state': {
          request: async () => (quiet ? { scrollY: shownScroll.current } : {}),
        },
        // Inside Chat this is the App itself, never its public copy: a public action sends only from
        // the public link (src/apps/public), where the page answers these two itself.
        'public.status': { request: async () => ({ public: false }) },
        'public.submit': {
          request: async () => {
            throw Object.assign(new Error('PUBLIC_LINK_ONLY'), { code: 'PUBLIC_LINK_ONLY' });
          },
        },
        // The App names its sync once (sdk.server.showSync): its table and the saved function that syncs it. Only names.
        'runtime.sync': {
          request: async (input: unknown) => {
            const { tableId, name } = (input ?? {}) as { tableId?: unknown; name?: unknown };
            if (!syncName(tableId) || !syncName(name))
              throw Object.assign(new Error('INVALID_INPUT'), { code: 'INVALID_INPUT' });
            setSync((now) =>
              now?.tableId === tableId && now.name === name ? now : { tableId, name },
            );
            return null;
          },
        },
        'runtime.ready': {
          request: async () => {
            // The App has shown: the canvas may now load what it deferred (its Share chip).
            // Only the shown document's ready counts: a replaced frame's late one never takes its successor's.
            if (shown.current?.issued.ticket === issuedTicket) {
              readyFor.current = issuedTicket;
              setReadyTicket(issuedTicket);
            }
            presentation.current.settled?.();
            return { ready: true };
          },
        },
        'runtime.locale': { request: async () => presentation.current.locale },
        // The frame follows the page's theme; nothing else is sent (no datasets: none are registered).
        'runtime.presentation': {
          subscribe: (_args: unknown, emit: (event: unknown) => void) => {
            const root = document.documentElement;
            const send = () =>
              emit({ theme: root.classList.contains('dark') ? 'dark' : 'light', embedded: true });
            send();
            const observer = new MutationObserver(send);
            observer.observe(root, { attributes: true, attributeFilter: ['class'] });
            return () => observer.disconnect();
          },
        },
        // The App's own boundary caught a render failure (runtime.ready may already have been sent): the
        // frame is withheld and a truthful failure shown, never a generic fallback that looks rendered. Only
        // the document this handler set was built for may do so; a replaced frame's late error is ignored.
        'runtime.error': {
          request: async () => {
            if (alive.current && issuedTicket)
              setIssue((now) =>
                now.status === 'ready' && now.issued.ticket === issuedTicket
                  ? { status: 'failed', retryable: true }
                  : now,
              );
            return null;
          },
        },
      } as unknown as RuntimeHandlers,
    };
  }, [
    cachedSeed,
    quiet,
    issuedTicket,
    issuedNonce,
    session,
    artifactId,
    revisionId,
    versionId,
    purpose,
    writeApproval.approve,
  ]);
  // How current the cached view is, for the quiet line in the App's top bar.
  const view = useSyncExternalStore(
    cache ? cache.subscribe : noCachedView,
    cache ? cache.getSnapshot : noCachedSnapshot,
  );
  // A replaced document or session, or leaving the App, obsoletes the old cached reads: an in-flight read of the
  // previous document can never answer this one or borrow its source. Declared after the store subscription, so
  // the bar has already moved to the new cache when the old one is retired.
  useEffect(() => () => cache?.invalidate(), [cache]);
  // The shown document, for work that finishes later: a Refresh or Sync now reopens what is shown then, never a
  // document that has since been replaced or retired.
  const shown = useRef<{
    issued: RuntimeIssued;
    handlers: RuntimeHandlers;
    attempt: number;
    quiet: boolean;
  } | null>(null);
  shown.current =
    issue.status === 'ready' && !invalidSeed
      ? { issued: issue.issued, handlers, attempt: issue.attempt, quiet: issue.quiet }
      : null;
  // The App's last verified sync, read as this document (the read the App itself may make). A refused read (no
  // table-wide read for this viewer) means no line; it is read again for each new document.
  const readSync = useCallback(
    async (tableId: string) => {
      const value = (await handlers['functions.syncStatus']?.request?.({ tableId })) as {
        ok?: unknown;
        lastSyncedAt?: unknown;
      } | null;
      if (!value || value.ok === false || !('lastSyncedAt' in value)) return undefined;
      return typeof value.lastSyncedAt === 'string' ? value.lastSyncedAt : null;
    },
    [handlers],
  );
  // For who can Sync now, each document also looks, without starting anything, for a Sync now not yet settled: the
  // one kept here across a reopen, or one the server finds (after a full reload, or from another tab). It is kept
  // and said; an outcome found this way frees it but never reloads the App: only a click does.
  useEffect(() => {
    // Between documents (opening, or a failed one) the line keeps what it last knew. A document reads only once it
    // has opened: a Sync now reopen keeps its sync, and the new document's reads would come before its ticket is
    // redeemed, and be refused.
    if (!sync || !issuedNonce || !issuedTicket || readyTicket !== issuedTicket) return;
    let live = true;
    const asked = syncActivity.current;
    const kept = keptFor(sync);
    const look =
      canSync && syncOperation
        ? Promise.resolve(syncOperation.request(syncInput(sync, 'observe', kept?.requestId))).catch(
            () => null,
          )
        : null;
    Promise.all([readSync(sync.tableId), look]).then(
      ([fetchedAt, result]) => {
        if (!live || !alive.current) return;
        if (fetchedAt === undefined) return setSynced(null);
        // A click since the look asked (started, or answered) knows more: the look's answer is out of date.
        const overtaken = {
          now: syncing.current || syncActivity.current !== asked,
          syncing: syncing.current,
        };
        const reply = overtaken.now ? null : (result as SyncReply);
        if (reply && reply.ok !== false)
          syncOriginal.current = nextOriginal(syncOriginal.current, sync, reply, kept?.requestId);
        // A run found verifiably synced is the table's last sync (the server names it only then).
        const at = verifiedSync(reply, sync.tableId) ?? fetchedAt;
        setSynced((shown) => lookedLine(shown, at, reply, overtaken));
      },
      () => {
        if (live && alive.current) setSynced(null);
      },
    );
    return () => {
      live = false;
    };
    // keptFor reads the kept original when the document asks; it is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sync, readSync, canSync, syncOperation, issuedNonce, issuedTicket, readyTicket]);
  const deckNow =
    issue.status === 'ready' && (issue.issued as RuntimeIssued & { deck?: boolean }).deck === true;
  useEffect(() => onDeck?.(deckNow), [deckNow, onDeck]);
  if (issue.status === 'loading')
    return <AppOpening artifactId={artifactId} name={title} preparing={issue.preparing} />;
  if (issue.status === 'none-built')
    // Neutral, not an error, and no Try again: it cannot succeed. Data, History and Versions stay usable.
    return (
      <p className="ws-page-note" role="status" data-testid="app-no-built-version">
        {copy.appNoBuiltVersion}
      </p>
    );
  if (issue.status === 'failed' || invalidSeed)
    return (
      <AppCannotOpen
        artifactId={artifactId}
        name={title}
        reason={
          issue.status === 'failed' && issue.code
            ? (checkMessage(issue.code, issue.reason, sharingCopy) ??
              appRuntimeErrorMessage(issue.code, errorCopy))
            : undefined
        }
        onRetry={
          issue.status === 'failed' &&
          issue.retryable &&
          appRuntimeErrorKind(issue.code) !== 'access'
            ? () => setAttempt((n) => n + 1)
            : undefined
        }
      />
    );
  // A chat-built deck offers its exports in the canvas header, for exactly this saved version.
  const deck = (issue.issued as RuntimeIssued & { deck?: boolean }).deck === true;
  // The person's Refresh or confirmed Sync now reopens the App (#3874): the shown view stays, with its own age, until
  // the reopened one is ready.
  const reopen = (fetchedAt: string | null) => {
    const now = shown.current;
    if (!now || holding.current) return;
    holding.current = true;
    setHeld({ ...now, fetchedAt });
    setAttempt((n) => n + 1);
  };
  // Sync now runs the App's own saved sync function as this person. While its original has not verifiably settled,
  // Sync now only asks for that run (the server answers with its outcome); a new run needs a settled one. Only the
  // run's verified sync of this table reopens the App; anything else keeps the last good rows and says what is known.
  const syncNow = (target: SyncTarget, shownAt: string | null) => {
    syncing.current = true;
    syncActivity.current += 1;
    const kept = keptFor(target);
    const sent = kept ? kept.requestId : globalThis.crypto.randomUUID();
    syncOriginal.current = { ...target, ...(sent ? { requestId: sent } : {}) };
    const from = shown.current?.issued;
    const show = (next: SyncedLine) => {
      if (alive.current) setSynced(next);
    };
    // `unconfirmed`: not known to have failed (it may have run), so it is not said as a failure.
    const fail = (unconfirmed: boolean, code?: unknown) =>
      show({ fetchedAt: shownAt, status: 'failed', unconfirmed, code });
    setSynced({ fetchedAt: shownAt, status: 'refreshing' });
    void Promise.resolve(
      syncOperation?.request(syncInput(target, kept ? 'observe' : 'start', sent)),
    )
      .then((result) => {
        const reply = result as SyncReply;
        if (reply?.ok === false) throw reply.error ?? { code: 'UNAVAILABLE' };
        syncOriginal.current = nextOriginal(syncOriginal.current, target, reply, sent);
        if (reply?.status === 'failed') return fail(false, reply.code);
        // Nothing was pending after all (a run the server found has gone): the line is as before.
        if (reply?.status === 'idle') return show({ fetchedAt: shownAt, status: 'idle' });
        const at = verifiedSync(reply, target.tableId);
        if (!at) return fail(true);
        show({ fetchedAt: at, status: 'idle' });
        // Only the document Sync now was asked from reopens: a late reply never reloads its successor.
        if (alive.current && shown.current?.issued === from) reopen(shownAt);
      })
      // A refusal the reader can act on is said in its words; anything only "unavailable" (a lost reply, a server
      // failure after the run was claimed) is not known to have failed. Either way the original stays kept.
      .catch((error: unknown) => {
        const code = (error as { code?: unknown } | null)?.code;
        fail(appRuntimeErrorKind(code) === 'unavailable', code);
      })
      .finally(() => {
        syncing.current = false;
        syncActivity.current += 1;
      });
  };
  // While a Refresh reopens, the shown frame stays above the reopened one until that one reports ready.
  const swapping = held !== null && held.attempt !== issue.attempt;
  const frames = [
    ...(swapping ? [{ ...held, current: false }] : []),
    { issued: issue.issued, handlers, attempt: issue.attempt, quiet: issue.quiet, current: true },
  ];
  return (
    <>
      {writeApproval.dialog}
      {deck && (purpose === 'editor' || purpose === 'viewer') && (
        <DeckExport title={title} version={{ artifactId, revisionId, versionId, purpose }} />
      )}
      {/* An App that syncs into its own tables says when it last synced, with Sync now for who can run it. */}
      {sync && synced && (
        <CanvasHeaderTools>
          <ViewFreshness
            value={{
              kind: 'sync',
              fetchedAt: held ? held.fetchedAt : synced.fetchedAt,
              status: held ? 'refreshing' : synced.status,
              unconfirmed: synced.unconfirmed,
              ...(canSync ? { refresh: () => syncNow(sync, synced.fetchedAt) } : {}),
              ...(synced.status === 'failed'
                ? {
                    hint: synced.unconfirmed
                      ? freshnessCopy.syncUnconfirmed
                      : appRuntimeErrorMessage(synced.code, errorCopy),
                  }
                : {}),
            }}
          />
        </CanvasHeaderTools>
      )}
      {/* Only an App whose reads are cached has a "how current" to say; a refresh failure is kept quiet there. */}
      {cache &&
        !(sync && synced && view.status === 'idle') &&
        (view.fetchedAt || view.status !== 'idle') && (
          <CanvasHeaderTools>
            <ViewFreshness
              value={{
                fetchedAt: held ? held.fetchedAt : view.fetchedAt,
                // Until a reopened view has shown, the bar still says it is updating.
                status: held ? 'refreshing' : view.status,
                // The person's Refresh reopens the App from the refreshed cache: the new values paint at once and the bar
                // shows that batch's time, with the shown view kept until the reopened one is ready. Nothing reloads a
                // view without this click; background refreshes only update the cache.
                refresh: () =>
                  void cache
                    .refresh()
                    .then(() => {
                      if (alive.current) reopen(view.fetchedAt);
                    })
                    .catch(() => {}),
                // A source that refused the Refresh: the view stays, and the bar says how to get it back.
                ...(view.reason === 'connection' ? { hint: errorCopy.connection } : {}),
              }}
            />
          </CanvasHeaderTools>
        )}
      <div className="ws-runtime-swap" data-swap={swapping ? '' : undefined}>
        {frames.map((frame) => (
          <RuntimeFrame
            key={frame.attempt}
            mount={mount}
            issued={frame.issued}
            handlers={frame.handlers}
            renew={(scope) =>
              control(session, artifactId, 'renew', {
                revisionId,
                versionId,
                purpose,
                documentNonce: String((scope as { documentNonce?: string }).documentNonce ?? ''),
              })
            }
            retire={(scope) =>
              control(session, artifactId, 'revoke', {
                revisionId,
                versionId,
                purpose,
                documentNonce: String((scope as { documentNonce?: string }).documentNonce ?? ''),
              })
            }
            title={title}
            // A Refresh-reopened document opens behind the shown view: no opening stage to fade at the swap.
            opening={frame.quiet ? undefined : <AppOpening artifactId={artifactId} name={title} />}
            onInvalidated={frame.current ? onInvalidated : undefined}
            onReady={frame.current && held ? release : undefined}
            // The reopening view, behind the shown one, takes no keyboard focus, clicks or screen-reader reading.
            inert={swapping && frame.current}
            onReopen={() => setAttempt((n) => n + 1)}
          />
        ))}
      </div>
    </>
  );
}

/** The host hook, with the runtime mount injected so tests never need a real frame. */
export function createSelectedAppHost({
  mount,
  checks = (selection, session) => {
    const api = createCheckReportApi(selection, session);
    return {
      read: api.read,
      ...(selection.purpose === 'editor' ? { scan: api.scan, acknowledge: api.acknowledge } : {}),
    };
  },
  sources = (selection, session) => createAppSourcesApi(selection, session),
  readyTimeoutMs = READY_TIMEOUT_MS,
}: {
  /** How long an issued frame may stay silent before it is shown as unable to open (tests shorten it). */
  readyTimeoutMs?: number;
  mount: MountNamedRuntime;
  /** The check read (and, for editors, the scan) of one exact selection on the frame's bound session. */
  checks?: (selection: CheckSelection, session: typeof fetch) => CheckReportReader;
  /** The sources/refresh authoring of one exact EDITOR selection on the frame's bound session. */
  sources?: (
    selection: Parameters<typeof createAppSourcesApi>[0],
    session: typeof fetch,
  ) => AppSourcesApi;
}): UseSelectedAppHost {
  return function useSelectedAppHost(input: SelectedAppHostInput): SelectedAppHost | null {
    const workspace = useOptionalWorkspace();
    // Bound ONCE per selected App mount: the whole frame lifecycle (bootstrap, renew, retire) rides the
    // session it was opened in, and ends terminally with it.
    const session = useRef(useSessionBoundFetch()).current;
    const { target, display } = input;
    const revisionId = 'revisionId' in target && target.revisionId ? target.revisionId : null;
    const versionId = 'versionId' in target && target.versionId ? target.versionId : null;
    const purpose = 'purpose' in target && target.purpose ? target.purpose : null;
    // The whole original selection is the lifecycle key: a different version or purpose of the same
    // revision is another document, never the same frame.
    const key = `${input.companyId}\0${input.actorId}\0${target.id}\0${revisionId}\0${versionId}\0${purpose}`;
    // The Data tab: the native Data read of exactly this selection, on the session this App opened in.
    // A draft target (no saved tuple) has none. It reads once the tab is first shown: opening the App
    // loads only the App, never every collection behind a tab the person may not open.
    const [dataShown, setDataShown] = useState(false);
    // Whether the opened document is a Deck, learned from its own issued document (never from a name or a label).
    const [deckFor, setDeckFor] = useState<string | null>(null);
    const isDeck = deckFor === key;
    const onDeck = useCallback((deck: boolean) => setDeckFor(deck ? key : null), [key]);
    const data = useNativeData(
      dataShown && revisionId && versionId && (purpose === 'viewer' || purpose === 'editor')
        ? { artifactId: target.id, revisionId, versionId, purpose }
        : null,
      session,
    );
    // The Checks reader belongs to this exact selection and the session it opened in.
    const checkRead = useMemo(
      () =>
        revisionId && versionId && (purpose === 'viewer' || purpose === 'editor')
          ? checks({ artifactId: target.id, revisionId, versionId, purpose }, session)
          : null,
      [key], // eslint-disable-line react-hooks/exhaustive-deps
    );
    // Sources and refresh time: an editor's change to this exact editor version (the route requires editor).
    const sourcesApi = useMemo(
      () =>
        revisionId &&
        versionId &&
        purpose === 'editor' &&
        (display?.rights.role === 'owner' || display?.rights.role === 'editor')
          ? sources({ artifactId: target.id, revisionId, versionId, purpose }, session)
          : null,
      [key, display?.rights.role], // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Review (a chat notice's "Review") opens THIS App on its Versions tab at the Checks: no new tab or route. The
    // target object is the request token, so it also applies when the same App is already open elsewhere.
    // The notice's Review sends {tab:'versions', step:'checks'}; {tab:'checks'} is accepted too (same intent).
    const reviewChecks =
      ('step' in target && target.step === 'checks') ||
      ('tab' in target && target.tab === 'checks');
    // One SDK/document lifetime per selection: the image viewer and the frame close with it.
    const lifetime = useMemo(() => {
      const subscribers = new Set<() => void>();
      return {
        onInvalidate: (callback: () => void) => {
          subscribers.add(callback);
          return () => void subscribers.delete(callback);
        },
        invalidate: () => [...subscribers].forEach((callback) => callback()),
      };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    useLayoutEffect(() => () => lifetime.invalidate(), [lifetime]);
    const sharing = useAppSharing({
      selection: revisionId && versionId ? { artifactId: target.id, revisionId, versionId } : null,
      session,
      // Share is the canvas header's one Share button (AppSharingChip); the
      // App's own menu keeps Unshare.
      canShare: false,
      canUnshare: display?.rights.canUnshare === true,
      title: display?.app.name,
      changed: () => {
        input.onSharingChanged?.();
      },
    });
    return useMemo(() => {
      if (!revisionId || !versionId || !purpose) return null; // a draft target has no saved version to open
      return {
        lifetime,
        appFrame: (
          <SavedAppFrame
            key={key}
            owner={{ companyId: input.companyId, actorId: input.actorId }}
            selection={{ artifactId: target.id, revisionId, versionId, purpose }}
            title={display?.app.name ?? ''}
            session={session}
            mount={mount}
            onInvalidated={lifetime.invalidate}
            onDeck={onDeck}
            canSync={Boolean(display) && display?.rights.role !== 'user'}
            readyTimeoutMs={readyTimeoutMs}
          />
        ),
        data,
        deck: isDeck,
        onTabChange: (tab: string) => {
          if (tab === 'data') setDataShown(true);
        },
        ...(reviewChecks ? { initialTab: 'versions' as const, revealToken: target } : {}),
        history: null,
        versions: null,
        completedVersions:
          purpose === 'editor' ? (
            <CompletedAppVersions
              key={key}
              selection={{ artifactId: target.id, versionId, revisionId }}
              session={session}
              changed={(saved) => {
                if (workspace)
                  void workspace.openCanvas({
                    ...target,
                    id: saved.artifactId,
                    versionId: saved.versionId,
                    revisionId: saved.revisionId,
                    purpose: 'editor',
                  } as never);
              }}
            />
          ) : undefined,
        // Owners and editors only; the version is the selected one, never a different current version.
        versionsControls:
          checkRead ||
          sourcesApi ||
          display?.rights.role === 'owner' ||
          display?.rights.role === 'editor' ? (
            <>
              {display?.rights.role === 'owner' || display?.rights.role === 'editor' ? (
                <AppControlsMount
                  key={`${key}\0controls`}
                  artifactId={target.id}
                  versionId={versionId}
                />
              ) : null}
              {sourcesApi ? <AppSourcesSection key={`${key}\0sources`} api={sourcesApi} /> : null}
              {checkRead ? (
                <AppChecksMount
                  key={`${key}\0checks`}
                  checks={checkRead}
                  reveal={reviewChecks ? target : undefined}
                />
              ) : null}
            </>
          ) : undefined,
        hostOverlays: sharing.overlay,
        on: {
          ...(sharing.onUnshare ? { onUnshare: sharing.onUnshare } : {}),
        },
      };
    }, [
      key,
      lifetime,
      revisionId,
      versionId,
      purpose,
      display?.app.name,
      display?.rights.role,
      session,
      workspace,
      data,
      reviewChecks,
      target,
      checkRead,
      sourcesApi,
      isDeck,
      onDeck,
      sharing,
    ]); // eslint-disable-line react-hooks/exhaustive-deps
  };
}

export const useSelectedAppHost = createSelectedAppHost({
  mount: mountNamedRuntime as unknown as MountNamedRuntime,
});
